import { useState, useMemo, useCallback } from "react";
import {
  Container,
  Column,
  Tag,
  MUIBox,
  MUITypography,
  TableV2,
} from "@cobee/cobee-ui";
import qs from "qs";
import { useTranslation } from "react-i18next";
import { useTreatments } from "@splitsoftware/splitio-react";
import { useLocation, useHistory } from "react-router-dom";
import { RequestStates } from "v2/domain/benefitRequest/entities/Request";
import CenterLoader from "v2/ui/components/CenterLoader";
import EmptyBlock from "v2/ui/components/EmptyBlock";
import { BenefitTypeEnum } from "v2/domain/common/entities/BenefitType";
import { BenefitCategory } from "v2/domain/common/entities/BenefitCagetory";
import featureFlagsDictionary from "infrastructure/split/feature-flags-dictionary";
import useGetCompany from "v2/ui/hooks/common/useGetCompany";
import useBenefitRequests from "../../hooks/queries/useBenefitRequests";
import styles from "./styles";
import RequestFilters from "../../components/RequestFilters";

// eslint-disable-next-line no-shadow
enum Variants {
  WARNING = "warning",
  DISABLED = "disabled",
  ERROR = "error",
  SUCCESS = "success",
}

const config = {
  [RequestStates.PENDING]: Variants.WARNING,
  [RequestStates.CANCELLED]: Variants.DISABLED,
  [RequestStates.DENIED]: Variants.ERROR,
  [RequestStates.APPROVED]: Variants.SUCCESS,
};

export type StateSelect = {
  id: RequestStates;
  text: string;
};

export type CategorySelect = {
  id: BenefitTypeEnum | BenefitCategory;
  text: string;
};

export default function RequestsPage() {
  const { cellStyle, containerCell } = styles;

  const { t } = useTranslation();
  const history = useHistory();
  const { search } = useLocation();
  const { data: company } = useGetCompany();
  const params = qs.parse(search, { ignoreQueryPrefix: true });

  const { companyId, state: stateUrl, category: categoryUrl } = params;
  const treatments = useTreatments(
    [
      featureFlagsDictionary.BI_2174,
      featureFlagsDictionary.ENT_259,
      featureFlagsDictionary.BI_3777,
    ],
    {
      companyId: company?.id ?? "",
      countryISOCode: company?.countryIsoCode ?? "",
    }
  );

  const isTrainingRequestFFEnabled =
    treatments[featureFlagsDictionary.BI_3777].treatment === "on";

  const statesConfig: StateSelect[] = useMemo(
    () => [
      { id: RequestStates.PENDING, text: t("v2.common.pending") },
      {
        id: RequestStates.CANCELLED,
        text: t("v2.benefit-requests.statuses.cancelled"),
      },
      {
        id: RequestStates.DENIED,
        text: t("v2.benefit-requests.statuses.denied"),
      },
      {
        id: RequestStates.APPROVED,
        text: t("v2.benefit-requests.statuses.approved"),
      },
    ],
    [t]
  );

  const trainingCategorySelect = useMemo(
    () =>
      isTrainingRequestFFEnabled
        ? [
            {
              id: BenefitCategory.TRAINING,
              text: t("v2.common.benefit-type.training"),
            },
          ]
        : [],
    [t, isTrainingRequestFFEnabled]
  );

  const categoriesConfig: CategorySelect[] = useMemo(
    () => [
      ...trainingCategorySelect,
      {
        id: BenefitTypeEnum.IT_PRODUCT_BENEFIT,
        text: t("v2.common.benefit-type.it-product-benefit"),
      },
      {
        id: BenefitTypeEnum.RENTING_BENEFIT,
        text: t("v2.common.benefit-type.renting-benefit"),
      },
      {
        id: BenefitTypeEnum.RETIREMENT_INSURANCE_BENEFIT,
        text: t("v2.common.benefit-type.retirement-insurance-benefit"),
      },
      {
        id: BenefitTypeEnum.PENSION_PLAN_BENEFIT,
        text: t("v2.common.benefit-type.pension-plan-benefit"),
      },
    ],
    [t, trainingCategorySelect]
  );

  const [selectedStates, setSelectedStates] = useState<string[]>(
    stateUrl
      ? statesConfig
          .filter(({ id }) => stateUrl.indexOf(id) !== -1)
          .map(({ text }) => text)
      : []
  );

  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    categoryUrl
      ? categoriesConfig
          .filter(({ id }) => categoryUrl.indexOf(id) !== -1)
          .map(({ text }) => text)
      : []
  );

  const stateParams = useMemo(
    () =>
      statesConfig
        .filter(({ text }) => selectedStates.indexOf(text) !== -1)
        .map(({ id }) => id),
    [selectedStates, statesConfig]
  );

  const categoriesParams = useMemo(
    () =>
      categoriesConfig
        .filter(({ text }) => selectedCategories.indexOf(text) !== -1)
        .map(({ id }) => id),
    [categoriesConfig, selectedCategories]
  );

  const { data, isFetching, refetch } = useBenefitRequests({
    companyId,
    states: stateParams,
    categories: categoriesParams,
  });

  const parseState = (state: RequestStates): Variants => {
    return config[state] || Variants.SUCCESS;
  };

  const statesDictionary = {
    [RequestStates.PENDING]: t("v2.common.pending"),
    [RequestStates.CANCELLED]: t("v2.benefit-requests.statuses.cancelled"),
    [RequestStates.DENIED]: t("v2.benefit-requests.statuses.denied"),
    [RequestStates.APPROVED]: t("v2.benefit-requests.statuses.approved"),
  };

  const redirectToRequestDetail = useCallback(
    ({ category, id, recurrentPaymentId }) => {
      const categoryName = category.split("-benefit")[0];

      const isFeatureFlagOn =
        treatments[featureFlagsDictionary.BI_2174].treatment === "on";

      const isRentingFeatureFlagOn =
        treatments[featureFlagsDictionary.ENT_259].treatment === "on";

      if (
        categoryName === BenefitCategory.TRAINING &&
        !isTrainingRequestFFEnabled
      ) {
        window.location.href = `${window.location.protocol}//${window.location.host}/benefits/recurrent-payments/education/${recurrentPaymentId}`;
        return undefined;
      }

      if (
        (isRentingFeatureFlagOn && categoryName === BenefitCategory.RENTING) ||
        (categoryName === BenefitCategory.TRAINING &&
          isTrainingRequestFFEnabled) ||
        categoryName === BenefitCategory.IT_PRODUCT ||
        (isFeatureFlagOn &&
          categoryName === BenefitCategory.RETIREMENT_INSURANCE)
      ) {
        window.location.href = `${window.location.protocol}//${window.location.host}/v2/benefits/${categoryName}/request/${id}`;
        return undefined;
      }

      return history.push(`/v2/requests/detail/${id}`);
    },
    [history, treatments, isTrainingRequestFFEnabled]
  );

  const columns: Column[] = [
    {
      id: "legalId",
      label: t("v2.common.legal-id"),
      cellComponent: ({
        value: {
          id,
          employee: { legalId },
          category,
          details: { recurrentPaymentId },
        },
      }) => {
        return (
          <MUIBox
            sx={containerCell}
            onClick={() =>
              redirectToRequestDetail({ category, id, recurrentPaymentId })
            }
          >
            <MUITypography sx={cellStyle}>{legalId}</MUITypography>
          </MUIBox>
        );
      },
    },
    {
      id: "name",
      label: t("v2.common.name"),
      cellComponent: ({
        value: {
          id,
          employee: { name, surname },
          category,
          details: { recurrentPaymentId },
        },
      }) => (
        <MUIBox
          sx={containerCell}
          onClick={() =>
            redirectToRequestDetail({ category, id, recurrentPaymentId })
          }
        >
          <MUITypography sx={cellStyle}>{`${name} ${surname}`}</MUITypography>
        </MUIBox>
      ),
    },
    {
      id: "email",
      label: t("v2.common.email"),
      cellComponent: ({
        value: {
          id,
          employee: { email },
          category,
          details: { recurrentPaymentId },
        },
      }) => (
        <MUIBox
          sx={containerCell}
          onClick={() =>
            redirectToRequestDetail({ category, id, recurrentPaymentId })
          }
        >
          <MUITypography sx={cellStyle}>{email}</MUITypography>
        </MUIBox>
      ),
    },
    {
      id: "initialDate",
      label: t("v2.common.application-date"),
      cellComponent: ({
        value: {
          initialDate,
          id,
          category,
          details: { recurrentPaymentId },
        },
      }) => (
        <MUIBox
          sx={containerCell}
          onClick={() =>
            redirectToRequestDetail({ category, id, recurrentPaymentId })
          }
        >
          <MUITypography sx={cellStyle}>
            {initialDate.toLocaleDateString()}
          </MUITypography>
        </MUIBox>
      ),
    },
    {
      id: "category",
      label: t("v2.common.benefit"),
      cellComponent: ({
        value: {
          category,
          id,
          details: { recurrentPaymentId },
        },
      }) => (
        <MUIBox
          sx={containerCell}
          onClick={() =>
            redirectToRequestDetail({ category, id, recurrentPaymentId })
          }
        >
          <MUITypography sx={cellStyle}>
            {t(`v2.common.benefit-type.${category}`)}
          </MUITypography>
        </MUIBox>
      ),
    },
    {
      id: "state",
      label: t("v2.common.state"),
      cellComponent: ({
        value: {
          state,
          id,
          category,
          details: { recurrentPaymentId },
        },
      }) => (
        <MUIBox
          sx={containerCell}
          onClick={() =>
            redirectToRequestDetail({ category, id, recurrentPaymentId })
          }
        >
          <Tag text={statesDictionary[state]} variant={parseState(state)} />
        </MUIBox>
      ),
    },
  ];

  const onChangeFilter = ({ type, value }) => {
    const onChangeConfig = {
      state: setSelectedStates,
      category: setSelectedCategories,
    };

    onChangeConfig[type](value);
  };

  if (isFetching) {
    return (
      <Container>
        <CenterLoader />
      </Container>
    );
  }

  return (
    <Container paddingStyle="20px">
      {!isFetching && data?.length === 0 && (
        <>
          <RequestFilters
            companyId={companyId}
            statesConfig={statesConfig}
            categoriesConfig={categoriesConfig}
            selectedStates={selectedStates}
            selectedCategories={selectedCategories}
            refetch={refetch}
            handleChangeFilter={onChangeFilter}
          />
          <EmptyBlock
            title={t("v2.benefit-requests.no-requests-found")}
            subtitle={t("v2.benefit-requests.try-searching-with-other-filters")}
          />
        </>
      )}
      {!isFetching && data && data?.length > 0 && (
        <>
          <RequestFilters
            statesConfig={statesConfig}
            companyId={companyId}
            selectedStates={selectedStates}
            categoriesConfig={categoriesConfig}
            selectedCategories={selectedCategories}
            refetch={refetch}
            handleChangeFilter={onChangeFilter}
          />
          <TableV2
            columns={columns}
            rows={data}
            sortInitiallyBy={["initialDate", "desc"]}
            paginationProps={{
              labelRowsPerPage: t("v2.benefit-requests.request-per-page"),
              labelDisplayedRows: ({ from, to, count }) => {
                return `${from}–${to} ${t("v2.common.of")} ${count}`;
              },
            }}
          />
        </>
      )}
    </Container>
  );
}
